/**
 * ioLiberum Outlook Add-in — Office JS Service Layer
 * Wraps Office.context.mailbox interactions for clean component access.
 */

let officeReady = false;
let officeContext = null;

/**
 * Initialize Office JS. Call once in main.jsx.
 * @returns {Promise<{host: string, platform: string}>}
 */
export function initOffice() {
    return new Promise((resolve) => {
        if (typeof Office !== 'undefined' && Office.onReady) {
            Office.onReady((info) => {
                officeReady = true;
                officeContext = info;
                resolve(info);
            });
        } else {
            // Running outside Office (dev mode) — resolve with mock
            officeReady = false;
            officeContext = { host: 'dev', platform: 'dev' };
            resolve(officeContext);
        }
    });
}

/**
 * Check if running inside Office.
 */
export function isOfficeReady() {
    return officeReady;
}

/**
 * Get the current Outlook item ID (EWS format).
 * Returns null if not in Office or no item context.
 */
export function getItemId() {
    if (!officeReady) return null;
    try {
        return Office.context.mailbox?.item?.itemId || null;
    } catch {
        return null;
    }
}

/**
 * Get the current conversation ID.
 */
export function getConversationId() {
    if (!officeReady) return null;
    try {
        return Office.context.mailbox?.item?.conversationId || null;
    } catch {
        return null;
    }
}

/**
 * Get the internetMessageId of the read item.
 */
export function getInternetMessageId() {
    if (!officeReady) return null;
    try {
        return Office.context.mailbox?.item?.internetMessageId || null;
    } catch {
        return null;
    }
}

/**
 * Get the inReplyTo internet message ID of the compose item.
 */
export function getInReplyToId() {
    if (!officeReady) return null;
    try {
        return Office.context.mailbox?.item?.inReplyTo || null;
    } catch {
        return null;
    }
}

/**
 * Forces Outlook to save the current compose item to Drafts.
 * This instructs the Exchange server to generate an itemId and conversationId
 * for a brand new email, allowing us to accurately track the email natively
 * without relying on categories or hidden internet headers.
 * @returns {Promise<string|null>} The generated conversationId
 */
export function saveDraftAndGetConversationId() {
    return new Promise((resolve) => {
        if (!officeReady) {
            resolve('dev-conversation-12345');
            return;
        }

        try {
            const item = Office.context.mailbox.item;
            if (typeof item.saveAsync !== 'function') {
                resolve(item.conversationId || null);
                return;
            }

            // If it already has a conversation ID (e.g. reply), just return it
            if (item.conversationId) {
                resolve(item.conversationId);
                return;
            }

            item.saveAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    // Result.value contains the itemId natively
                    resolve(item.conversationId || result.value);
                } else {
                    resolve(item.conversationId || null);
                }
            });
        } catch {
            resolve(null);
        }
    });
}

/**
 * Get the current user's email address.
 */
export function getUserEmail() {
    if (!officeReady) return 'nick@ioliberum.com'; // Fallback for dev
    try {
        return Office.context.mailbox?.userProfile?.emailAddress || 'nick@ioliberum.com';
    } catch {
        return 'nick@ioliberum.com';
    }
}

/**
 * Detect which surface the add-in is running in.
 * @returns {'reading_pane' | 'compose' | 'calendar' | 'task_pane'}
 */
export function getSurface() {
    if (!officeReady) return 'reading_pane'; // Dev default

    try {
        const item = Office.context.mailbox?.item;
        if (!item) return 'task_pane';

        // Check item type
        const itemType = item.itemType;
        if (itemType === Office.MailboxEnums.ItemType.Appointment) {
            return 'calendar';
        }

        // Check if compose or read mode
        if (item.itemClass === 'IPM.Note' && item.displayReplyForm) {
            return 'reading_pane';
        }

        // Check for compose-specific properties
        if (typeof item.to !== 'undefined' && typeof item.body?.setAsync === 'function') {
            return 'compose';
        }

        return 'reading_pane';
    } catch {
        return 'reading_pane';
    }
}

/**
 * Get email sender info from reading pane.
 * @returns {Promise<{email: string, name: string} | null>}
 */
export function getSenderInfo() {
    if (!officeReady) {
        return Promise.resolve({ email: 'john@smithllc.com', name: 'John Smith' });
    }

    try {
        const item = Office.context.mailbox?.item;
        if (!item || !item.from) return Promise.resolve(null);

        return Promise.resolve({
            email: item.from.emailAddress,
            name: item.from.displayName,
        });
    } catch {
        return Promise.resolve(null);
    }
}

/**
 * Get email subject from current item.
 */
export function getSubject() {
    if (!officeReady) return 'Re: Operating Agreement — revision notes';
    try {
        return Office.context.mailbox?.item?.subject || '';
    } catch {
        return '';
    }
}

/**
 * Register a listener for when the user switches items (Pinning support).
 * @param {Function} callback 
 * @returns {Function} function to remove the listener
 */
export function onItemChanged(callback) {
    if (!officeReady || !Office.context.mailbox || !Office.context.mailbox.addHandlerAsync) {
        return () => { };
    }

    const handler = () => {
        callback();
    };

    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
            console.error("Failed to add ItemChanged handler", result.error?.message);
        }
    });

    return () => {
        Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, { handler });
    };
}

/**
 * Insert text into compose body.
 * @param {string} text
 * @returns {Promise<void>}
 */
export function insertDraftBody(text) {
    return new Promise((resolve, reject) => {
        if (!officeReady) {
            resolve(); // Dev noop
            return;
        }
        try {
            Office.context.mailbox.item.body.prependAsync(
                text,
                { coercionType: Office.CoercionType.Html },
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve();
                    } else {
                        reject(new Error(result.error?.message || 'Failed to set body'));
                    }
                }
            );
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Add file attachment from base64 (Compose surface only).
 * @param {string} base64Content
 * @param {string} filename
 * @returns {Promise<void>}
 */
export function addAttachmentFromBase64(base64Content, filename) {
    return new Promise((resolve, reject) => {
        if (!officeReady) {
            resolve(); // Dev noop
            return;
        }
        try {
            Office.context.mailbox.item.addFileAttachmentFromBase64Async(
                base64Content,
                filename,
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve();
                    } else {
                        reject(new Error(result.error?.message || 'Failed to attach file'));
                    }
                }
            );
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Get current attachments on the compose item.
 * Returns an array of { name, size, contentType, isInline } objects.
 * @returns {Promise<Array>}
 */
export function getComposeAttachments() {
    return new Promise((resolve) => {
        if (!officeReady) {
            resolve([]); // Dev noop
            return;
        }
        try {
            Office.context.mailbox.item.getAttachmentsAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value || []);
                } else {
                    resolve([]);
                }
            });
        } catch {
            resolve([]);
        }
    });
}

/**
 * Get user settings from Office roaming settings.
 * @returns {object} The user settings object.
 */
export function getSettings() {
    if (!officeReady || !Office.context.roamingSettings) {
        // Dev defaults or no-op
        const local = localStorage.getItem('io_addin_settings');
        return local ? JSON.parse(local) : {};
    }

    try {
        const stored = Office.context.roamingSettings.get('ioLiberumSettings');
        return stored ? JSON.parse(stored) : {};
    } catch (err) {
        console.error("Failed to load settings from roastingSettings:", err);
        return {};
    }
}

/**
 * Save user settings to Office roaming settings.
 * @param {object} settingsObj 
 * @returns {Promise<void>}
 */
export function saveSettings(settingsObj) {
    if (!officeReady || !Office.context.roamingSettings) {
        localStorage.setItem('io_addin_settings', JSON.stringify(settingsObj));
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        try {
            Office.context.roamingSettings.set('ioLiberumSettings', JSON.stringify(settingsObj));
            Office.context.roamingSettings.saveAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                } else {
                    reject(new Error(result.error?.message || 'Failed to save settings'));
                }
            });
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Add a recipient to the To, CC, or BCC field of a compose item.
 * @param {string} email - The email address to add
 * @param {string} name - The display name
 * @param {'to'|'cc'|'bcc'} field - Which field to add to
 * @returns {Promise<void>}
 */
export function addRecipient(email, name, field = 'to') {
    return new Promise((resolve, reject) => {
        if (!officeReady) {
            console.log(`[Dev] addRecipient: ${field} → ${name} <${email}>`);
            resolve();
            return;
        }
        try {
            const item = Office.context.mailbox.item;
            const recipientField = field === 'cc' ? item.cc : field === 'bcc' ? item.bcc : item.to;
            if (!recipientField || typeof recipientField.addAsync !== 'function') {
                reject(new Error(`Recipient field '${field}' not available`));
                return;
            }
            recipientField.addAsync([{ emailAddress: email, displayName: name || email }], (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                } else {
                    reject(new Error(result.error?.message || `Failed to add recipient to ${field}`));
                }
            });
        } catch (err) {
            reject(err);
        }
    });
}

/**
 * Appends an invisible tracking tag to the message right as it sends.
 * This ensures the Matter ID natively survives Exchange formatting and is effortlessly
 * readable by N8N triggers directly in the body.content HTML output.
 * @param {string|null} matterId - The matter ID to embed.
 * @returns {Promise<void>}
 */
export function setMatterInternetHeader(matterId) {
    return new Promise((resolve, reject) => {
        if (!officeReady) {
            console.log(`[Dev] applyTracking: Matter ID = ${matterId}`);
            resolve();
            return;
        }

        try {
            const item = Office.context.mailbox.item;
            
            // 1. Append invisible tracking tag to HTML body
            if (item.body && typeof item.body.appendOnSendAsync === 'function') {
                if (matterId) {
                    const invisibleHtml = `<span id="ioliberum-matter-tag" data-matter="${matterId}" style="display:none!important; color:transparent; width:0; height:0; font-size:0; overflow:hidden;" aria-hidden="true"></span>`;
                    item.body.appendOnSendAsync(invisibleHtml, { coercionType: Office.CoercionType.Html }, () => {
                        // We intentionally ignore result here to not block
                    });
                }
            }
            
            // 2. Append [matterId] to the subject line for Plain-Text email fallback support
            if (item.subject && typeof item.subject.getAsync === 'function') {
                item.subject.getAsync((subRes) => {
                    if (subRes.status === Office.AsyncResultStatus.Succeeded) {
                        let subject = subRes.value || '';
                        
                        // Strip out any trailing bracketed numbers (our tag)
                        subject = subject.replace(/\s*\[\d+\]$/, '');
                        
                        // Inject the new tag if provided
                        if (matterId) {
                            subject = `${subject} [${matterId}]`;
                        }
                        
                        item.subject.setAsync(subject, () => {
                            resolve(); // Resolve when subject modification is finalized
                        });
                    } else {
                        resolve();
                    }
                });
            } else {
                resolve();
            }
        } catch (err) {
            console.error('Exception setting appendOnSend tracking pixel:', err);
            resolve();
        }
    });
}
